import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Language,
} from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, Chip, InputAdornment, TextField } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDemoForm } from '../../../context/DemoFormContext';
import { flagEmojiFromCode } from '../../../helpers/countryFlag';

export interface CountryFilterAutocompleteProps {
  /**
   * If true, render the input with an error state and helper text. Used to
   * surface "at least one country required" validation when sort mode is
   * Country Popularity.
   */
  error?: boolean;
  errorText?: string;
}

const arraysShallowEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Decide which placeholder to show in the country picker:
 * - "Loading…" while the apiCountries dictionary is still being fetched
 * - "Countries" when the picker is empty and ready for input
 * - empty string once at least one country chip is rendered
 */
const computeCountryPlaceholder = (apiCountriesReady: boolean, hasSelection: boolean): string => {
  if (apiCountriesReady && hasSelection) return '';
  if (apiCountriesReady) return 'Countries';
  return 'Loading…';
};

const CountryFilterAutocomplete: React.FC<CountryFilterAutocompleteProps> = ({
  error,
  errorText,
}) => {
  const { apiCountries, countries, setFormCountries } = useDemoForm();
  const [open, setOpen] = useState(false);
  // The country code → flag lookup needs `apiCountries` (loaded async via
  // React Query). On a hard refresh that data isn't there yet, so the chip
  // would render with the full country name briefly, then flip to "🇺🇸 US"
  // once the query resolves. Hold off chip rendering until the dictionary
  // is populated to avoid that flicker.
  const apiCountriesReady = Boolean(apiCountries && Object.keys(apiCountries).length > 0);
  // While the dropdown is open, all selection changes accumulate locally so
  // the channels list only re-fetches once the user clicks out.
  const committed = useMemo(() => countries || [], [countries]);
  const [draft, setDraft] = useState<string[]>(committed);

  // Keep the draft in sync with the committed value whenever the
  // committed selection changes. This covers two cases:
  //   • Dropdown closed: external resets / list-type changes / default
  //     backfill flow into the visible state immediately.
  //   • Dropdown open: an external reset (e.g. another control clearing
  //     countries) would otherwise be silently overwritten on close by
  //     the user's stale draft. Always syncing means the system-of-record
  //     wins; the user can simply re-pick if they want their selection
  //     back.
  // User input from inside this autocomplete goes via `handleChange`
  // and updates `draft` directly without round-tripping through
  // `committed`, so this effect doesn't fight live edits.
  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  const options = useMemo(() => Object.keys(apiCountries || {}), [apiCountries]);

  const handleOpen = useCallback(() => {
    setDraft(committed);
    setOpen(true);
  }, [committed]);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (!arraysShallowEqual(draft, committed)) {
      setFormCountries(draft);
    }
  }, [draft, committed, setFormCountries]);

  const handleChange = useCallback(
    (_e: React.SyntheticEvent, newValue: string[]) => {
      if (open) {
        // Defer commit until close — only mutate local draft.
        setDraft(newValue);
      } else {
        // Edits while closed (e.g. chip delete X) commit immediately since
        // there is no close event to coalesce against.
        setFormCountries(newValue);
      }
    },
    [open, setFormCountries],
  );

  const formatFullLabel = useCallback(
    (option: string) => {
      const flag = flagEmojiFromCode(apiCountries?.[option]?.country_code);
      return flag ? `${flag} ${option}` : option;
    },
    [apiCountries],
  );

  const formatShortChip = useCallback(
    (option: string) => {
      const code = apiCountries?.[option]?.country_code?.toUpperCase();
      const flag = flagEmojiFromCode(apiCountries?.[option]?.country_code);
      if (code && flag) return `${flag} ${code}`;
      if (code) return code;
      return option;
    },
    [apiCountries],
  );

  // The visible value tracks the draft when the dropdown is open, so the user
  // sees their pending selection live; when closed, it shows the committed set.
  const visibleValue = open ? draft : committed;

  const chipSx = {
    height: '20px',
    fontSize: '0.7rem',
    '& .MuiChip-label': { px: 0.75 },
    '& .MuiChip-deleteIcon': { fontSize: '14px', mr: 0.25 },
  } as const;

  return (
    <Autocomplete<string, true, true, false>
      multiple
      size="small"
      disableCloseOnSelect
      disableClearable
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      options={options}
      value={visibleValue}
      onChange={handleChange}
      renderOption={(optionProps, option, { selected }) => {
        const { key, ...rest } = optionProps as React.HTMLAttributes<HTMLLIElement> & {
          key: React.Key;
        };
        return (
          <li key={key} {...rest}>
            <Checkbox
              size="small"
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              checked={selected}
              sx={{ mr: 0.5, p: 0.5 }}
            />
            {formatFullLabel(option)}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        // While `apiCountries` is still loading, suppress chip rendering so
        // we don't flash full country names before the flag/code form is
        // available. The autocomplete value remains intact internally.
        if (!apiCountriesReady) return [];
        // Show every selected country at all times — never collapse to a
        // "+N" summary. Always use the compact (flag + 2-char code) form;
        // the dropdown options below are where the user can see the full
        // country names when picking.
        return tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const label = formatShortChip(option);
          return (
            <Chip
              key={key}
              label={label}
              color="primary"
              variant="outlined"
              size="small"
              sx={chipSx}
              {...tagProps}
            />
          );
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          error={Boolean(error)}
          helperText={error ? errorText : undefined}
          placeholder={computeCountryPlaceholder(apiCountriesReady, visibleValue.length > 0)}
          // Tell every flavor of password manager to skip this input.
          // 1Password (data-1p-ignore), LastPass (data-lpignore),
          // Bitwarden / generic (data-form-type, autoComplete=off) were
          // all latching onto the autocomplete and overlaying their
          // autofill icon on top of the chips.
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
            'data-1p-ignore': 'true',
            'data-lpignore': 'true',
            'data-form-type': 'other',
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start" sx={{ ml: 0.5, mr: 0 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>
                    <Language fontSize="small" />
                  </Box>
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        width: '100%',
        // Always allow chips to wrap — never cap height. The input grows
        // vertically as more countries are selected so the user can see
        // every chip without overflow truncation.
        '& .MuiOutlinedInput-root': {
          flexWrap: 'wrap',
          minHeight: 40,
        },
      }}
    />
  );
};

export default CountryFilterAutocomplete;
